/**
 * Run / artifact retention policy (Delivery roadmap Tier 2.2).
 *
 * Per-org policy describing how long we keep run rows + their
 * artifacts (screenshots, traces, HARs) before the nightly purger
 * deletes them.
 *
 * Defaults shipped 2026-05-10:
 *   - free  plan : capped at 30 days
 *   - team  plan : capped at 180 days
 *   - enterprise : unlimited (operator can opt-in to a value)
 *
 * The cap is enforced server-side; the API rejects PUTs that exceed
 * the plan ceiling with `RUN_RETENTION_PLAN_CAP_EXCEEDED`.
 */
import { z } from 'zod';

export const RUN_RETENTION_MIN_DAYS = 1;
export const RUN_RETENTION_MAX_DAYS = 3650; // 10 years — covers regulated industries.
export const RUN_RETENTION_DEFAULT_RUN_DAYS = 90;
export const RUN_RETENTION_DEFAULT_SCREENSHOT_DAYS = 90;
export const RUN_RETENTION_DEFAULT_TRACE_DAYS = 30;
export const RUN_RETENTION_DEFAULT_HAR_DAYS = 14;

export const RunRetentionPolicySchema = z.object({
  /** Days to keep run rows + step logs. After this we soft-delete. */
  runDays: z
    .number()
    .int()
    .min(RUN_RETENTION_MIN_DAYS)
    .max(RUN_RETENTION_MAX_DAYS)
    .default(RUN_RETENTION_DEFAULT_RUN_DAYS),
  /** Days to keep screenshots (object storage). Screenshots referenced
   *  by an active visual baseline are exempt. */
  screenshotDays: z
    .number()
    .int()
    .min(RUN_RETENTION_MIN_DAYS)
    .max(RUN_RETENTION_MAX_DAYS)
    .default(RUN_RETENTION_DEFAULT_SCREENSHOT_DAYS),
  /** Days to keep Playwright trace.zip files. */
  traceDays: z
    .number()
    .int()
    .min(RUN_RETENTION_MIN_DAYS)
    .max(RUN_RETENTION_MAX_DAYS)
    .default(RUN_RETENTION_DEFAULT_TRACE_DAYS),
  /** Days to keep .har files (network capture). */
  harDays: z
    .number()
    .int()
    .min(RUN_RETENTION_MIN_DAYS)
    .max(RUN_RETENTION_MAX_DAYS)
    .default(RUN_RETENTION_DEFAULT_HAR_DAYS),
  /** When true, deleted runs survive 30 days as soft-deleted rows
   *  before final purge. Useful for compliance teams who want to
   *  reconstruct a deletion. */
  softDelete: z.boolean().default(true),
});
export type RunRetentionPolicy = z.infer<typeof RunRetentionPolicySchema>;

export const PutRunRetentionRequestSchema = RunRetentionPolicySchema;
export type PutRunRetentionRequest = z.infer<typeof PutRunRetentionRequestSchema>;

export const RunRetentionResponseSchema = z.object({
  orgId: z.string().uuid(),
  policy: RunRetentionPolicySchema,
  /** Audit metadata for the most recent change. */
  updatedAt: z.string(),
  updatedBy: z.string().nullable(),
  /** Estimated GB saved per month at this policy vs current usage. */
  estimatedMonthlySavingsBytes: z.number().int().nullable(),
  /** Hard caps imposed by the org's billing plan. */
  planCaps: z.object({
    runDaysMax: z.number().int().nullable(),
    screenshotDaysMax: z.number().int().nullable(),
    traceDaysMax: z.number().int().nullable(),
    harDaysMax: z.number().int().nullable(),
  }),
});
export type RunRetentionResponse = z.infer<typeof RunRetentionResponseSchema>;
